import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Checkbox, Input, Modal, Select, Space, Table, Tag } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { ArrowLeftOutlined, CloudSyncOutlined, CloudUploadOutlined, DeleteOutlined, EditOutlined, FileAddOutlined, PlayCircleOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import MwsEnvironmentSelector, { MWS_ENVIRONMENT_OPTIONS } from '../components/MwsEnvironmentSelector';
import ScriptExecutionService from '../http/ScriptService';
import { DEPLOY_ENV } from '../utils/config';
import { ScriptExecution, ScriptExecutionCommandType, ScriptExecutionFile, ScriptExecutionInfo, ScriptExecutionRunResult, ScriptExecutionServer } from '../entities/Script';
import './ScriptExecutionPage.css';

const ScriptExecutionPage = (): React.JSX.Element => {
    document.title = '脚本管理';
    const navigate = useNavigate();
    const [scripts, setScripts] = useState<ScriptExecutionInfo[]>([]);
    const [selectedScriptName, setSelectedScriptName] = useState('');
    const [script, setScript] = useState<ScriptExecution | null>(null);
    const [scriptPath, setScriptPath] = useState('');
    const [defaultCommand, setDefaultCommand] = useState('');
    const [commandTemplates, setCommandTemplates] = useState('');
    const [scriptVersion, setScriptVersion] = useState(1);
    const [content, setContent] = useState('');
    const [scriptFiles, setScriptFiles] = useState<ScriptExecutionFile[]>([]);
    const [servers, setServers] = useState<ScriptExecutionServer[]>([]);
    const [selectedIps, setSelectedIps] = useState<string[]>([]);
    const [manualServerIps, setManualServerIps] = useState('');
    const [allServers, setAllServers] = useState(true);
    const [commandType, setCommandType] = useState<ScriptExecutionCommandType>('SCRIPT');
    const [command, setCommand] = useState('');
    const [deployBeforeRun, setDeployBeforeRun] = useState(false);
    const [results, setResults] = useState<ScriptExecutionRunResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [newScriptName, setNewScriptName] = useState('');
    const [newScriptPath, setNewScriptPath] = useState('');
    const [newDefaultCommand, setNewDefaultCommand] = useState('');
    const [newCommandTemplates, setNewCommandTemplates] = useState('');
    const [newScriptContent, setNewScriptContent] = useState('');
    const [fileOpen, setFileOpen] = useState(false);
    const [editingFileName, setEditingFileName] = useState('');
    const [fileName, setFileName] = useState('');
    const [fileTargetPath, setFileTargetPath] = useState('');
    const [fileContent, setFileContent] = useState('');
    const [syncOpen, setSyncOpen] = useState(false);
    const getDefaultSyncEnvironments = () => DEPLOY_ENV === 'prod' ? ['test'] : ['prod'];
    const [syncEnvironments, setSyncEnvironments] = useState<string[]>(getDefaultSyncEnvironments());
    const syncEnvironmentOptions = MWS_ENVIRONMENT_OPTIONS.filter(option => option.value !== DEPLOY_ENV);

    const parseManualServerIps = (value: string) => value
        .split(/[\s,，]+/)
        .map((item) => item.trim())
        .filter(Boolean);
    const getTargetIps = () => {
        const registeredIps = allServers ? servers.map((server) => server.internalIp) : selectedIps;
        return Array.from(new Set([...registeredIps, ...parseManualServerIps(manualServerIps)]));
    };
    const selectedCount = getTargetIps().length;
    const commandTemplateOptions = (() => {
        try {
            const templates = commandTemplates ? JSON.parse(commandTemplates) : {};
            return Object.keys(templates).map((key) => ({ label: key, value: key as ScriptExecutionCommandType }));
        } catch {
            return [];
        }
    })();

    const clearScript = () => {
        setSelectedScriptName('');
        setScript(null);
        setScriptPath('');
        setDefaultCommand('');
        setCommandTemplates('');
        setScriptVersion(1);
        setContent('');
        setScriptFiles([]);
        setServers([]);
        setSelectedIps([]);
        setManualServerIps('');
        setResults([]);
    };

    const loadScript = useCallback(async (scriptName: string) => {
        setLoading(true);
        try {
            const [scriptData, serverData, fileData] = await Promise.all([
                ScriptExecutionService.getScript({ environment: DEPLOY_ENV, scriptName }),
                ScriptExecutionService.listServers({ environment: DEPLOY_ENV, scriptName }),
                ScriptExecutionService.listFiles({ environment: DEPLOY_ENV, scriptName }),
            ]);
            setSelectedScriptName(scriptName);
            setScript(scriptData);
            setScriptPath(scriptData?.scriptPath || '');
            setDefaultCommand(scriptData?.defaultCommand || '');
            setCommandTemplates(scriptData?.commandTemplates || '');
            setScriptVersion(scriptData?.scriptVersion || 1);
            setContent(scriptData?.content || '');
            setScriptFiles(fileData);
            setServers(serverData);
            setSelectedIps(serverData.map((server) => server.internalIp));
            setManualServerIps('');
            setResults([]);
            setCommand('');
            setCommandType('SCRIPT');
        } finally {
            setLoading(false);
        }
    }, []);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const scriptList = await ScriptExecutionService.listScripts({ environment: DEPLOY_ENV });
            setScripts(scriptList);
            const nextScriptName = selectedScriptName || scriptList[0]?.scriptName || '';
            if (nextScriptName) {
                await loadScript(nextScriptName);
            } else {
                clearScript();
            }
        } finally {
            setLoading(false);
        }
    }, [loadScript, selectedScriptName]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const createScript = async () => {
        setLoading(true);
        try {
            const response = await ScriptExecutionService.createScript({
                environment: DEPLOY_ENV,
                scriptName: newScriptName,
                scriptPath: newScriptPath,
                defaultCommand: newDefaultCommand,
                commandTemplates: newCommandTemplates,
                content: newScriptContent,
            });
            if (response.isFailed()) {
                return;
            }
            setCreateOpen(false);
            setNewScriptName('');
            setNewScriptPath('');
            setNewDefaultCommand('');
            setNewCommandTemplates('');
            setNewScriptContent('');
            await refresh();
            await loadScript(response.data.scriptName);
            toast.success('脚本已创建');
        } finally {
            setLoading(false);
        }
    };

    const openFileModal = (file?: ScriptExecutionFile) => {
        if (file) {
            setEditingFileName(file.fileName);
            setFileName(file.fileName);
            setFileTargetPath(file.targetPath);
            setFileContent(file.content);
        } else {
            setEditingFileName('');
            setFileName('');
            setFileTargetPath('');
            setFileContent('');
        }
        setFileOpen(true);
    };

    const saveFile = async () => {
        if (!ensureScript()) {
            return;
        }
        setLoading(true);
        try {
            if (editingFileName && editingFileName !== fileName) {
                await ScriptExecutionService.deleteFile({ environment: DEPLOY_ENV, scriptName: selectedScriptName, fileName: editingFileName });
            }
            const response = await ScriptExecutionService.saveFile({
                environment: DEPLOY_ENV,
                scriptName: selectedScriptName,
                fileName,
                targetPath: fileTargetPath,
                content: fileContent,
            });
            if (response.isFailed()) {
                return;
            }
            setFileOpen(false);
            setScriptFiles(await ScriptExecutionService.listFiles({ environment: DEPLOY_ENV, scriptName: selectedScriptName }));
            toast.success('依赖文件已保存');
        } finally {
            setLoading(false);
        }
    };

    const deleteFile = async (file: ScriptExecutionFile) => {
        if (!ensureScript() || !window.confirm(`删除依赖文件 ${file.fileName}？`)) {
            return;
        }
        setLoading(true);
        try {
            const response = await ScriptExecutionService.deleteFile({ environment: DEPLOY_ENV, scriptName: selectedScriptName, fileName: file.fileName });
            if (response.isFailed()) {
                return;
            }
            setScriptFiles(await ScriptExecutionService.listFiles({ environment: DEPLOY_ENV, scriptName: selectedScriptName }));
            toast.success('依赖文件已删除');
        } finally {
            setLoading(false);
        }
    };

    const openSyncModal = () => {
        setSyncEnvironments(getDefaultSyncEnvironments().filter(env => env !== DEPLOY_ENV));
        setSyncOpen(true);
    };

    const syncScript = async () => {
        if (!ensureScript()) {
            return;
        }
        if (syncEnvironments.length === 0) {
            toast.error('请选择同步环境');
            return;
        }
        setLoading(true);
        try {
            const response = await ScriptExecutionService.syncScript({
                environment: DEPLOY_ENV,
                scriptName: selectedScriptName,
                targetEnvironments: syncEnvironments,
            });
            if (response.isFailed()) {
                return;
            }
            setSyncOpen(false);
            toast.success(`已同步到 ${syncEnvironments.join(', ')}`);
            await refresh();
        } finally {
            setLoading(false);
        }
    };

    const buildTargetRequest = () => ({
        environment: DEPLOY_ENV,
        scriptName: selectedScriptName,
        allServers: allServers && parseManualServerIps(manualServerIps).length === 0,
        serverIps: getTargetIps(),
    });

    const ensureScript = () => {
        if (!script?.id || !selectedScriptName) {
            toast.error('请选择脚本');
            return false;
        }
        return true;
    };

    const ensureTarget = () => {
        if (!ensureScript()) {
            return false;
        }
        if (selectedCount === 0) {
            toast.error('请选择服务器');
            return false;
        }
        return true;
    };

    const saveScript = async () => {
        if (!ensureScript()) {
            return;
        }
        setLoading(true);
        try {
            const response = await ScriptExecutionService.updateScript({ id: script!.id, scriptPath, defaultCommand, commandTemplates, content, environment: DEPLOY_ENV, scriptName: selectedScriptName });
            if (response.isFailed()) {
                return;
            }
            setScript(response.data);
            setScriptVersion(response.data.scriptVersion || 1);
            toast.success('脚本已保存');
            await refresh();
        } finally {
            setLoading(false);
        }
    };

    const deployScript = async () => {
        if (!ensureTarget()) {
            return;
        }
        setLoading(true);
        try {
            const data = await ScriptExecutionService.deployScript(buildTargetRequest());
            setResults(data);
        } finally {
            setLoading(false);
        }
    };

    const runScript = async () => {
        if (!ensureTarget()) {
            return;
        }
        setLoading(true);
        try {
            const data = await ScriptExecutionService.runScript({
                ...buildTargetRequest(),
                commandType,
                command,
                deployBeforeRun,
            });
            setResults(data);
        } finally {
            setLoading(false);
        }
    };

    const serverColumns = [
        {
            title: '服务器',
            dataIndex: 'instanceName',
            render: (value: string, record: ScriptExecutionServer) => value || record.internalIp,
        },
        {
            title: '内网IP',
            dataIndex: 'internalIp',
            width: 150,
        },
        {
            title: '公网IP',
            dataIndex: 'externalIp',
            width: 150,
        },
        {
            title: '状态',
            dataIndex: 'state',
            width: 90,
            render: (value: string) => <Tag color={value === 'ACTIVE' ? 'green' : 'default'}>{value}</Tag>,
        },
    ];

    const fileColumns = [
        {
            title: '文件',
            dataIndex: 'fileName',
            width: 150,
        },
        {
            title: '目标路径',
            dataIndex: 'targetPath',
            ellipsis: true,
        },
        {
            title: '版本',
            dataIndex: 'fileVersion',
            width: 80,
            render: (value: number) => <Tag color="blue">v{value || 1}</Tag>,
        },
        {
            title: '操作',
            width: 100,
            render: (_: unknown, record: ScriptExecutionFile) => (
                <Space size={4}>
                    <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openFileModal(record)} />
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => deleteFile(record)} />
                </Space>
            ),
        },
    ];

    const resultColumns = [
        {
            title: '服务器',
            dataIndex: 'internalIp',
            width: 170,
            render: (value: string, record: ScriptExecutionRunResult) => (
                <div>
                    <div>{record.instanceName || value}</div>
                    <div className="ssl-script-muted">{value}</div>
                </div>
            ),
        },
        {
            title: '命令',
            dataIndex: 'command',
            ellipsis: true,
        },
        {
            title: '版本',
            dataIndex: 'scriptVersion',
            width: 110,
            render: (value: number) => value || '-',
        },
        {
            title: '退出码',
            dataIndex: 'exitCode',
            width: 90,
            render: (value: number) => <Tag color={value === 0 ? 'green' : 'red'}>{value}</Tag>,
        },
    ];

    return (
        <div className="ssl-script-page">
            <div className="ssl-script-header">
                <div className="ssl-script-title">
                    <Button type="link" className="ssl-script-back" icon={<ArrowLeftOutlined />} onClick={() => navigate('/')} />
                    <span>脚本管理</span>
                    <MwsEnvironmentSelector prefix="nginx" enableDevHost={true} />
                </div>
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>刷新</Button>
                    <Button icon={<FileAddOutlined />} onClick={() => setCreateOpen(true)}>新增</Button>
                    <Button icon={<SaveOutlined />} type="primary" onClick={saveScript} loading={loading}>保存</Button>
                    <Button icon={<CloudSyncOutlined />} onClick={openSyncModal} loading={loading}>同步</Button>
                    <Button icon={<CloudUploadOutlined />} onClick={deployScript} loading={loading}>部署</Button>
                    <Button icon={<PlayCircleOutlined />} type="primary" danger={commandType === 'CERTBOT_RENEW'} onClick={runScript} loading={loading}>运行</Button>
                </Space>
            </div>

            <div className="ssl-script-meta">
                <Select
                    value={selectedScriptName || undefined}
                    placeholder="选择脚本"
                    onChange={loadScript}
                    options={scripts.map((item) => ({ label: item.scriptName, value: item.scriptName }))}
                />
                <Input addonBefore="路径" value={scriptPath} onChange={(event) => setScriptPath(event.target.value)} />
                <div className="ssl-script-version-box">
                    <span className="ssl-script-version-label">版本</span>
                    <Tag color="blue">v{scriptVersion}</Tag>
                </div>
                <div className="ssl-script-updated">更新时间：{script?.updateTime || '-'}</div>
            </div>

            <div className="ssl-script-workspace">
                <section className="ssl-script-editor">
                    <TextArea value={content} onChange={(event) => setContent(event.target.value)} spellCheck={false} placeholder="脚本内容存数据库里。这里先用简单代码文本框渲染。" />
                </section>

                <aside className="ssl-script-side">
                    <section className="ssl-script-panel">
                        <div className="ssl-script-panel-header">
                            <span>目标服务器</span>
                            <Tag>{selectedCount}</Tag>
                        </div>
                        <Checkbox checked={allServers} onChange={(event) => setAllServers(event.target.checked)}>全量运行</Checkbox>
                        <Table<ScriptExecutionServer>
                            className="ssl-script-server-table"
                            rowKey="internalIp"
                            size="small"
                            columns={serverColumns}
                            dataSource={servers}
                            pagination={false}
                            loading={loading}
                            rowSelection={allServers ? undefined : {
                                selectedRowKeys: selectedIps,
                                onChange: (keys) => setSelectedIps(keys.map(String)),
                            }}
                        />
                        <TextArea
                            className="ssl-script-manual-servers"
                            value={manualServerIps}
                            onChange={(event) => setManualServerIps(event.target.value)}
                            autoSize={{ minRows: 2, maxRows: 4 }}
                            placeholder="手动目标 IP，一行一个。新 nginx 服务器还没注册时填这里。"
                        />
                    </section>

                    <section className="ssl-script-panel">
                        <div className="ssl-script-panel-header">
                            <span>依赖文件</span>
                            <Button size="small" icon={<FileAddOutlined />} onClick={() => openFileModal()}>新增</Button>
                        </div>
                        <Table<ScriptExecutionFile>
                            rowKey="fileName"
                            size="small"
                            columns={fileColumns}
                            dataSource={scriptFiles}
                            pagination={false}
                            loading={loading}
                        />
                    </section>

                    <section className="ssl-script-panel">
                        <div className="ssl-script-panel-header">
                            <span>运行命令</span>
                        </div>
                        <Select<ScriptExecutionCommandType>
                            value={commandType}
                            onChange={setCommandType}
                            options={[
                                { label: '直接运行脚本', value: 'SCRIPT' },
                                ...commandTemplateOptions,
                            ]}
                        />
                        {commandType === 'SCRIPT' && (
                            <Input
                                value={command}
                                onChange={(event) => setCommand(event.target.value)}
                                placeholder="参数留空就直接运行脚本；例如：test 或 --env=test"
                            />
                        )}
                        <Checkbox checked={deployBeforeRun} onChange={(event) => setDeployBeforeRun(event.target.checked)}>运行前部署脚本</Checkbox>
                    </section>

                    <section className="ssl-script-panel">
                        <div className="ssl-script-panel-header">
                            <span>命令模板</span>
                        </div>
                        <div className="ssl-script-command-help">
                            <div className="ssl-script-command-help-title">给复杂命令用；普通 setup 脚本可以不用管</div>
                            <div>直接运行脚本时：空参数等于执行脚本路径；填参数时会拼成“脚本路径 参数”。模板里支持 ${'{scriptPath}'}、${'{env}'}、${'{command}'}。</div>
                        </div>
                        <Input
                            value={defaultCommand}
                            onChange={(event) => setDefaultCommand(event.target.value)}
                            placeholder={`默认命令，可空；例如 ${'$'}{scriptPath} ${'$'}{env}`}
                        />
                        <TextArea
                            value={commandTemplates}
                            onChange={(event) => setCommandTemplates(event.target.value)}
                            spellCheck={false}
                            placeholder='{"CERTBOT_RENEW":"certbot renew ..."}'
                        />
                    </section>
                </aside>
            </div>

            <section className="ssl-script-results">
                <div className="ssl-script-panel-header">
                    <span>运行结果</span>
                    <Tag>{results.length}</Tag>
                </div>
                <Table<ScriptExecutionRunResult>
                    rowKey={(record) => `${record.internalIp}-${record.command}-${record.exitCode}`}
                    size="small"
                    columns={resultColumns}
                    dataSource={results}
                    pagination={false}
                    expandable={{
                        expandedRowRender: (record) => (
                            <div className="ssl-script-output-grid">
                                <div>
                                    <div className="ssl-script-output-title">stdout</div>
                                    <pre>{record.output || '-'}</pre>
                                </div>
                                <div>
                                    <div className="ssl-script-output-title">stderr</div>
                                    <pre>{record.error || '-'}</pre>
                                </div>
                            </div>
                        ),
                    }}
                />
            </section>

            <Modal
                title="同步脚本"
                open={syncOpen}
                confirmLoading={loading}
                onCancel={() => setSyncOpen(false)}
                onOk={syncScript}
                okText="同步"
            >
                <div className="ssl-script-create-form">
                    <div className="ssl-script-muted">会把当前环境的主脚本和依赖文件完整同步到目标环境。</div>
                    <Select
                        mode="multiple"
                        value={syncEnvironments}
                        onChange={setSyncEnvironments}
                        options={syncEnvironmentOptions}
                    />
                </div>
            </Modal>

            <Modal
                title={editingFileName ? '编辑依赖文件' : '新增依赖文件'}
                open={fileOpen}
                confirmLoading={loading}
                onCancel={() => setFileOpen(false)}
                onOk={saveFile}
                width={900}
                okText="保存"
            >
                <div className="ssl-script-create-form">
                    <Input value={fileName} onChange={(event) => setFileName(event.target.value)} placeholder="文件名称，例如 service_cache.lua" />
                    <Input value={fileTargetPath} onChange={(event) => setFileTargetPath(event.target.value)} placeholder="目标路径，例如 /usr/local/openresty/lualib/service_cache.lua" />
                    <TextArea value={fileContent} onChange={(event) => setFileContent(event.target.value)} spellCheck={false} placeholder="文件内容" />
                </div>
            </Modal>

            <Modal
                title="新增脚本"
                open={createOpen}
                confirmLoading={loading}
                onCancel={() => setCreateOpen(false)}
                onOk={createScript}
                width={900}
                okText="创建"
            >
                <div className="ssl-script-create-form">
                    <Input value={newScriptName} onChange={(event) => setNewScriptName(event.target.value)} placeholder="脚本名称，例如 java.setup" />
                    <Input value={newScriptPath} onChange={(event) => setNewScriptPath(event.target.value)} placeholder="目标路径，例如 /usr/local/bin/setup-java.sh" />
                    <Input value={newDefaultCommand} onChange={(event) => setNewDefaultCommand(event.target.value)} placeholder={`默认命令，可选，例如 ${'$'}{scriptPath} --env=${'$'}{env}`} />
                    <TextArea value={newCommandTemplates} onChange={(event) => setNewCommandTemplates(event.target.value)} spellCheck={false} placeholder='命令模板 JSON，可选，例如 {"CERTBOT_RENEW":"certbot renew ..."}' />
                    <TextArea value={newScriptContent} onChange={(event) => setNewScriptContent(event.target.value)} spellCheck={false} placeholder="脚本内容" />
                </div>
            </Modal>
        </div>
    );
};

export default ScriptExecutionPage;
